import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import type { To } from 'react-router';
import { merge } from 'lodash-es';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import type { BadgeArrangement } from '../../../components/Badge/Badge';
import type { PortProps } from '../../../hardware/Port/Port';
import type { PortSpeed } from '../../../hardware/Port/utils';
import { Divider } from '../../../assets/Divider/Divider';
import { type IconName, Icon } from '../../../assets/Icon/Icon';
import { type VariantProp } from '../../../common/colors';
import { selectors } from '../../../common/selectors';
import { transitions } from '../../../common/transitions';
import { type TooltipProps, Tooltip } from '../../../components/Tooltip/Tooltip';
import { Button } from '../../../controls/Button/Button';
import {
  type PreviewCardHandle,
  PreviewCard,
  PreviewCardContent,
  PreviewCardTrigger,
} from '../../../formatting/PreviewCard/PreviewCard';
import { Port } from '../../../hardware/Port/Port';
import { colors, darkThemeSelector, keyframes, shadows, styled } from '../../../stitches.config';
import { Text } from '../../../text/Text';
import { AlignStack } from '../../../utilities/AlignStack/AlignStack';
import { heightCSS } from '../../../utilities/shared/Height';
import { maxHeightCSS } from '../../../utilities/shared/MaxHeight';
import { maxWidthCSS } from '../../../utilities/shared/MaxWidth';
import { minHeightCSS } from '../../../utilities/shared/MinHeight';
import { minWidthCSS } from '../../../utilities/shared/MinWidth';
import { widthCSS } from '../../../utilities/shared/Width';
import { VStack } from '../../../utilities/Stack/VStack';
import {
  type HopConnectionPreviewPayload,
  type HopPreview,
  type HopSharedProps,
  type HopsPreviewPayload,
  hopsVars,
} from './utils';

export type { HopConnectionPreviewPayload } from './utils';

type HopConnectionAnimateSpeed = 'slow' | 'normal' | 'fast';
type HopConnectionKind = 'error' | 'empty' | 'wired' | 'wireless';
type HopConnectionPath = 'arch' | 'flat';

/** Per-end dash speeds on dual-variant cables; resolved to one `HopConnectionPathFlow` using the slowest side. */
export type HopConnectionAnimateProp =
  | HopConnectionAnimateSpeed
  | { start?: HopConnectionAnimateSpeed; end?: HopConnectionAnimateSpeed };

type HopConnectionTrackSpeed = {
  animate: HopConnectionAnimateSpeed;
  variant: VariantProp;
};

const HOP_CONNECTION_TRACK_SPEED_MAP: Record<PortSpeed, HopConnectionTrackSpeed> = {
  10: { animate: 'slow', variant: 'attention' },
  100: { animate: 'slow', variant: 'attention' },
  1000: { animate: 'normal', variant: 'positive' },
  2500: { animate: 'normal', variant: 'positive' },
  10000: { animate: 'fast', variant: 'alternative' },
  25000: { animate: 'fast', variant: 'alternative' },
  50000: { animate: 'fast', variant: 'alternative' },
};

export type HopConnectionTrackVariantProp =
  | VariantProp
  | { start?: VariantProp; end?: VariantProp };

function isTrackDualVariant(
  variant: HopConnectionTrackVariantProp | undefined,
): variant is { start: VariantProp; end: VariantProp } {
  return (
    variant != null && typeof variant === 'object' && variant.start != null && variant.end != null
  );
}

type HopConnectionTracksProp = {
  animate?: HopConnectionAnimateProp;
  breaker?: HopConnectionBreakerProps;
  kind: HopConnectionKind;
  flow?: 'start-to-end' | 'end-to-start';
  speed?: PortSpeed;
  terminates?: 'start' | 'end';
  /**
   * Solid cable color, or `{ start, end }` for a single track whose stroke gradients from
   * the start-port side to the end-port side (both required for the gradient).
   */
  variant?: HopConnectionTrackVariantProp;
};

function hopAnimateDurationRank(s: HopConnectionAnimateSpeed): number {
  if (s === 'slow') return 3;
  if (s === 'normal') return 2;
  return 1;
}

/** Longest animation duration wins (slow > normal > fast). */
function slowestHopAnimateSpeed(
  ...candidates: (HopConnectionAnimateSpeed | undefined)[]
): HopConnectionAnimateSpeed | undefined {
  const defined = candidates.filter((x): x is HopConnectionAnimateSpeed => x != null);
  if (defined.length === 0) return undefined;
  return defined.reduce((a, b) => (hopAnimateDurationRank(b) > hopAnimateDurationRank(a) ? b : a));
}

function resolveTrackAnimate(
  track: HopConnectionTracksProp,
): HopConnectionAnimateSpeed | undefined {
  const speedValues = track.speed != null ? HOP_CONNECTION_TRACK_SPEED_MAP[track.speed] : undefined;
  const raw = track.animate;
  if (raw == null) return speedValues?.animate;
  if (typeof raw === 'string') return raw;
  return slowestHopAnimateSpeed(raw.start, raw.end, speedValues?.animate);
}

function connectionTrackWantsFlowAnimation(track: HopConnectionTracksProp | undefined): boolean {
  if (!track) return false;
  if (track.speed != null) return true;
  if (track.animate == null) return false;
  if (typeof track.animate === 'string') return true;
  return track.animate.start != null || track.animate.end != null;
}

function resolveTrackVariant(track: HopConnectionTracksProp): VariantProp {
  const { variant } = track;
  if (typeof variant === 'string') return variant;
  if (variant && typeof variant === 'object') {
    if (variant.end != null) return variant.end;
    if (variant.start != null) return variant.start;
  }
  if (track.speed != null) return HOP_CONNECTION_TRACK_SPEED_MAP[track.speed].variant;
  return 'neutral';
}

/** Base cable stroke colors — keep in sync with `HopConnectionPathBase` variant strokes. */
function hopConnectionPathBaseStroke(variant: VariantProp, mode: 'light' | 'dark'): string {
  const dark = mode === 'dark';
  switch (variant) {
    case 'alternative':
      return String(dark ? colors.blue900 : colors.blue100);
    case 'attention':
      return String(dark ? colors.amber900 : colors.amber100);
    case 'brand':
      return String(dark ? colors.brand900 : colors.brand100);
    case 'negative':
      return String(dark ? colors.red800 : colors.red100);
    case 'neutral':
      return String(dark ? colors.gray700 : colors.gray100);
    case 'positive':
      return String(dark ? colors.green900 : colors.green100);
    default:
      return String(dark ? colors.gray700 : colors.gray100);
  }
}

/** Flow / dash highlight colors — keep in sync with `HopConnectionPathFlow` variant strokes. */
function hopConnectionPathFlowStroke(variant: VariantProp, mode: 'light' | 'dark'): string {
  const dark = mode === 'dark';
  switch (variant) {
    case 'alternative':
      return String(dark ? colors.blue700 : colors.blue300);
    case 'attention':
      return String(dark ? colors.amber700 : colors.amber300);
    case 'brand':
      return String(dark ? colors.brand700 : colors.brand300);
    case 'negative':
      return String(dark ? colors.red600 : colors.red300);
    case 'neutral':
      return String(dark ? colors.gray600 : colors.gray200);
    case 'positive':
      return String(dark ? colors.green700 : colors.green300);
    default:
      return String(dark ? colors.gray600 : colors.gray200);
  }
}

/**
 * Gradient for the animated dash layer on dual-variant tracks (brighter stops than the base cable).
 */
function hopDualVariantFlowGradient(
  gradId: string,
  p0: Pt,
  p2: Pt,
  variantStart: VariantProp,
  variantEnd: VariantProp,
  opts?: { terminateEnd?: boolean },
): { gradientEl: ReactNode; styleEl: ReactNode } {
  const s0l = hopConnectionPathFlowStroke(variantStart, 'light');
  const s1l = hopConnectionPathFlowStroke(variantEnd, 'light');
  const s0d = hopConnectionPathFlowStroke(variantStart, 'dark');
  const s1d = hopConnectionPathFlowStroke(variantEnd, 'dark');
  const compress = opts?.terminateEnd === true;
  return {
    styleEl: (
      <style type="text/css">{`
        [id="${gradId}"] > stop.hop-dual-flow-s0 { stop-color: ${s0l}; }
        [id="${gradId}"] > stop.hop-dual-flow-s1 { stop-color: ${s1l}; }
        .m-dark [id="${gradId}"] > stop.hop-dual-flow-s0 { stop-color: ${s0d}; }
        .m-dark [id="${gradId}"] > stop.hop-dual-flow-s1 { stop-color: ${s1d}; }
      `}</style>
    ),
    gradientEl: (
      <linearGradient
        id={gradId}
        gradientUnits="userSpaceOnUse"
        x1={p0[0]}
        y1={p0[1]}
        x2={p2[0]}
        y2={p2[1]}
      >
        <stop className="hop-dual-flow-s0" offset="0%" />
        {compress ? (
          <>
            <stop className="hop-dual-flow-s1" offset="24%" />
            <stop className="hop-dual-flow-s1" offset="100%" />
          </>
        ) : (
          <stop className="hop-dual-flow-s1" offset="100%" />
        )}
      </linearGradient>
    ),
  };
}

/**
 * When `terminateEnd` is true, the cable is clipped before the geometric end of the path, so a
 * 0%→100% gradient never shows the “end” color. Compress the transition into the leading ~40% of
 * the gradient axis so both colors remain visible after clipping.
 */
function hopDualVariantTrackGradient(
  gradId: string,
  p0: Pt,
  p2: Pt,
  variantStart: VariantProp,
  variantEnd: VariantProp,
  opts?: { terminateEnd?: boolean },
): { gradientEl: ReactNode; styleEl: ReactNode } {
  const s0l = hopConnectionPathBaseStroke(variantStart, 'light');
  const s1l = hopConnectionPathBaseStroke(variantEnd, 'light');
  const s0d = hopConnectionPathBaseStroke(variantStart, 'dark');
  const s1d = hopConnectionPathBaseStroke(variantEnd, 'dark');
  const compress = opts?.terminateEnd === true;
  return {
    styleEl: (
      <style type="text/css">{`
        [id="${gradId}"] > stop.hop-dual-s0 { stop-color: ${s0l}; }
        [id="${gradId}"] > stop.hop-dual-s1 { stop-color: ${s1l}; }
        .m-dark [id="${gradId}"] > stop.hop-dual-s0 { stop-color: ${s0d}; }
        .m-dark [id="${gradId}"] > stop.hop-dual-s1 { stop-color: ${s1d}; }
      `}</style>
    ),
    gradientEl: (
      <linearGradient
        id={gradId}
        gradientUnits="userSpaceOnUse"
        x1={p0[0]}
        y1={p0[1]}
        x2={p2[0]}
        y2={p2[1]}
      >
        <stop className="hop-dual-s0" offset="0%" />
        {compress ? (
          <>
            <stop className="hop-dual-s1" offset="24%" />
            <stop className="hop-dual-s1" offset="100%" />
          </>
        ) : (
          <stop className="hop-dual-s1" offset="100%" />
        )}
      </linearGradient>
    ),
  };
}

export type HopConnectionProps = HopSharedProps & {
  type: 'connection';
  path?: HopConnectionPath;
  ports?: {
    start?: PortProps;
    end?: PortProps;
  };
  tracks?: {
    top?: HopConnectionTracksProp;
    bottom?: HopConnectionTracksProp;
  };
  /**
   * When set, connection breakers with a `preview` use this handle and omit nested `PreviewCard`;
   * `Hops` provides this automatically so all hop previews share one popup.
   */
  previewHandle?: PreviewCardHandle<HopsPreviewPayload>;
};

const hopCableFlow = keyframes({
  to: { strokeDashoffset: -100 },
});

const HOPS_TRACK_WIRELESS_DASH = '20 12';
const HOPS_TRACK_ZIGZAG_AMPLITUDE = 3;

type Pt = readonly [number, number];

type BezierTriple = { p0: Pt; cp: Pt; p2: Pt };

function pt(x: number, y: number): Pt {
  return [x, y];
}

const HOPS_PLANE_OFFSET = 3;
/** Row viewBox height / column viewBox width — matches `viewBox` minor axis for cable geometry. */
const HOP_SVG_MINOR = 40;

/** Breaker `top` % for row flat paired top track (line at y = 20 − offset). */
const HOP_FLAT_ROW_TOP_Y_PCT = `${((20 - HOPS_PLANE_OFFSET) / HOP_SVG_MINOR) * 100}%`;
/** Breaker `top` % for row flat paired bottom track (line at y = 20 + offset). */
const HOP_FLAT_ROW_BOTTOM_Y_PCT = `${((20 + HOPS_PLANE_OFFSET) / HOP_SVG_MINOR) * 100}%`;

const HOP_BEZIERS = {
  row: {
    top: { p0: pt(0, 20), cp: pt(50, -20), p2: pt(100, 20) },
    bottom: { p0: pt(0, 20), cp: pt(50, 60), p2: pt(100, 20) },
  },
  column: {
    top: { p0: pt(20, 0), cp: pt(60, 50), p2: pt(20, 100) },
    bottom: { p0: pt(20, 0), cp: pt(-20, 50), p2: pt(20, 100) },
  },
} satisfies {
  row: Record<'top' | 'bottom', BezierTriple>;
  column: Record<'top' | 'bottom', BezierTriple>;
};

const HOP_LINES = {
  row: {
    center: { p0: pt(0, 20), cp: pt(50, 20), p2: pt(100, 20) },
    top: {
      p0: pt(0, 20 - HOPS_PLANE_OFFSET),
      cp: pt(50, 20 - HOPS_PLANE_OFFSET),
      p2: pt(100, 20 - HOPS_PLANE_OFFSET),
    },
    bottom: {
      p0: pt(0, 20 + HOPS_PLANE_OFFSET),
      cp: pt(50, 20 + HOPS_PLANE_OFFSET),
      p2: pt(100, 20 + HOPS_PLANE_OFFSET),
    },
  },
  column: {
    center: { p0: pt(20, 0), cp: pt(20, 50), p2: pt(20, 100) },
    top: {
      p0: pt(20 + HOPS_PLANE_OFFSET, 0),
      cp: pt(20 + HOPS_PLANE_OFFSET, 50),
      p2: pt(20 + HOPS_PLANE_OFFSET, 100),
    },
    bottom: {
      p0: pt(20 - HOPS_PLANE_OFFSET, 0),
      cp: pt(20 - HOPS_PLANE_OFFSET, 50),
      p2: pt(20 - HOPS_PLANE_OFFSET, 100),
    },
  },
} satisfies {
  row: Record<'center' | 'top' | 'bottom', BezierTriple>;
  column: Record<'center' | 'top' | 'bottom', BezierTriple>;
};

function hopGeometry(
  direction: 'column' | 'row',
  trackKey: 'top' | 'bottom',
  path: HopConnectionPath = 'arch',
  paired: boolean = false,
) {
  if (path === 'flat') return HOP_LINES[direction][paired ? trackKey : 'center'];
  return HOP_BEZIERS[direction][trackKey];
}

function bezierD(p0: Pt, cp: Pt, p2: Pt) {
  return `M ${p0[0]} ${p0[1]} Q ${cp[0]} ${cp[1]} ${p2[0]} ${p2[1]}`;
}

function sampleBezier(p0: Pt, cp: Pt, p2: Pt, t: number) {
  const mt = 1 - t;
  return {
    x: mt * mt * p0[0] + 2 * mt * t * cp[0] + t * t * p2[0],
    y: mt * mt * p0[1] + 2 * mt * t * cp[1] + t * t * p2[1],
    tx: 2 * mt * (cp[0] - p0[0]) + 2 * t * (p2[0] - cp[0]),
    ty: 2 * mt * (cp[1] - p0[1]) + 2 * t * (p2[1] - cp[1]),
  };
}

function zigzagBezierD(p0: Pt, cp: Pt, p2: Pt, direction: 'row' | 'column', teeth: number) {
  const steps = teeth * 2;
  const points: { x: number; y: number }[] = [];

  for (let i = 0; i <= steps; i += 1) {
    const { x: bx, y: by } = sampleBezier(p0, cp, p2, i / steps);

    let sign = 0;
    if (i !== 0 && i !== steps) {
      sign = i % 2 === 0 ? 1 : -1;
    }
    const offset = HOPS_TRACK_ZIGZAG_AMPLITUDE * sign;
    points.push({
      x: bx + (direction === 'column' ? offset : 0),
      y: by + (direction === 'row' ? offset : 0),
    });
  }

  const parts = [`M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`];
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const mx = (prev.x + curr.x) / 2;
    const my = (prev.y + curr.y) / 2;
    parts.push(`Q ${prev.x.toFixed(2)} ${prev.y.toFixed(2)} ${mx.toFixed(2)} ${my.toFixed(2)}`);
  }
  const last = points[points.length - 1];
  parts.push(`L ${last.x.toFixed(2)} ${last.y.toFixed(2)}`);

  return parts.join(' ');
}

const HOPS_ZIGZAG_TIERS = [
  { teeth: 12, max: 250 },
  { teeth: 24, max: 500 },
  { teeth: 36, max: 800 },
  { teeth: 48, max: Infinity },
];

type ZigzagPathMap = Record<'row' | 'column', Record<'top' | 'bottom', string[]>>;

function zigzagTierPathsForTrack(
  direction: 'row' | 'column',
  trackKey: 'top' | 'bottom',
  pth: HopConnectionPath,
  paired: boolean,
): string[] {
  const { p0, cp, p2 } = hopGeometry(direction, trackKey, pth, paired);
  return HOPS_ZIGZAG_TIERS.map((tier) => zigzagBezierD(p0, cp, p2, direction, tier.teeth));
}

function buildZigzagPaths(pth: HopConnectionPath, paired: boolean): ZigzagPathMap {
  return {
    row: {
      top: zigzagTierPathsForTrack('row', 'top', pth, paired),
      bottom: zigzagTierPathsForTrack('row', 'bottom', pth, paired),
    },
    column: {
      top: zigzagTierPathsForTrack('column', 'top', pth, paired),
      bottom: zigzagTierPathsForTrack('column', 'bottom', pth, paired),
    },
  };
}

const HOPS_ZIGZAG_PATHS: Record<'single' | 'paired', Record<HopConnectionPath, ZigzagPathMap>> = {
  single: { arch: buildZigzagPaths('arch', false), flat: buildZigzagPaths('flat', false) },
  paired: { arch: buildZigzagPaths('arch', true), flat: buildZigzagPaths('flat', true) },
};

const HOPS_ZIGZAG_TIER_CSS: Record<
  'row' | 'column',
  Record<string, string | Record<string, string>>[]
> = {
  row: HOPS_ZIGZAG_TIERS.map((tier, idx) => {
    const prevMax = idx > 0 ? HOPS_ZIGZAG_TIERS[idx - 1].max : 0;
    const parts: string[] = [];
    if (prevMax > 0) parts.push(`(min-width: ${prevMax}px)`);
    if (Number.isFinite(tier.max)) parts.push(`(max-width: ${tier.max - 0.02}px)`);
    return { display: 'none', [`@container ${parts.join(' and ')}`]: { display: 'inline' } };
  }),
  column: HOPS_ZIGZAG_TIERS.map((tier, idx) => {
    const prevMax = idx > 0 ? HOPS_ZIGZAG_TIERS[idx - 1].max : 0;
    const parts: string[] = [];
    if (prevMax > 0) parts.push(`(min-height: ${prevMax}px)`);
    if (Number.isFinite(tier.max)) parts.push(`(max-height: ${tier.max - 0.02}px)`);
    return { display: 'none', [`@container ${parts.join(' and ')}`]: { display: 'inline' } };
  }),
};

const HOPS_TERMINATE_INSET = 14;
/** Cable stroke width in CSS px (`non-scaling-stroke`); used for cap-aware termination clip. */
const HOPS_CABLE_STROKE_PX = 4;
/** Length of the cut line across the cable (screen px). */
const HOPS_TERMINATE_CUT_SPAN_PX = 12;
/** Peak offset for a subtle zig-zag on the cut mark (screen px, mapped via sx/sy). */
const HOPS_TERMINATE_ZIG_AMP_PX = 2.4;
/** Shift the cut anchor slightly into the visible cable (px), away from port icons. */
const HOPS_TERMINATE_CENTER_NUDGE_PX = 0;
/** Stroke width for the termination mark (screen px). */
const HOPS_TERMINATION_STROKE = 2.4;
/**
 * Small extension past the tooth line for round cable caps at different Y (zig notches). Keep low —
 * large values push the track visibly past the cut; `termPad` was removed because the cut draws on top.
 */
const HOPS_TERMINATE_CLIP_MEET_PX = 1;

type HopSvgUserScale = { sx: number; sy: number };

function terminateCutAnchor(
  cx: number,
  cy: number,
  direction: 'row' | 'column',
  side: 'start' | 'end',
  svgUserScale: HopSvgUserScale | null,
): { ax: number; ay: number } {
  const sx = svgUserScale?.sx ?? (direction === 'row' ? 6 : 1.25);
  const sy = svgUserScale?.sy ?? (direction === 'row' ? 1.25 : 6);
  const nudgeSigned = (side === 'start' ? 1 : -1) * HOPS_TERMINATE_CENTER_NUDGE_PX;
  return {
    ax: direction === 'row' ? cx + nudgeSigned / sx : cx,
    ay: direction === 'column' ? cy + nudgeSigned / sy : cy,
  };
}

/**
 * For the symmetric W (x: 0, −d, +d, −d, 0), pick the first/last run along the span so every leg
 * has the same slope vs the cut axis: outer legs (−d, b) and (d, b) match inner legs (±2d, H/2−b).
 * That yields b = H/6 — equal **angle**, not equal length (length-equal splits made outer legs look
 * like they “sprawled” at a different tilt than the middle).
 */
function terminateZigEqualSlopeSplit(H: number): number {
  if (H <= 0) return 0;
  return Math.max(H / 6, 1e-6);
}

function terminateZigzagPathD(pts: string[]): string {
  if (pts.length === 0) return '';
  const tail = pts
    .slice(1)
    .map((p) => `L ${p}`)
    .join(' ');
  return `M ${pts[0]} ${tail}`;
}

/**
 * A **neutral cut** across the cable (perpendicular to the hop axis), with a slight zig-zag.
 * Geometry is mapped through `sx`/`sy` so amplitude stays ~constant in screen px under
 * `preserveAspectRatio="none"`.
 */
function terminateCutMarkD(
  cx: number,
  cy: number,
  direction: 'row' | 'column',
  side: 'start' | 'end',
  svgUserScale: HopSvgUserScale | null,
): string {
  const { ax, ay } = terminateCutAnchor(cx, cy, direction, side, svgUserScale);
  const sx = svgUserScale?.sx ?? (direction === 'row' ? 6 : 1.25);
  const sy = svgUserScale?.sy ?? (direction === 'row' ? 1.25 : 6);

  const halfSpan = HOPS_TERMINATE_CUT_SPAN_PX / 2;
  const amp = HOPS_TERMINATE_ZIG_AMP_PX;
  /** Flip zig so the two “teeth” (P1 & P3) sit on the cable side: row end = −x, row start = +x; column end = −y, column start = +y. */
  const h = side === 'end' ? 1 : -1;

  if (direction === 'row') {
    const y0 = ay - halfSpan / sy;
    const y1 = ay + halfSpan / sy;
    const H = y1 - y0;
    const d = amp / sx;
    const b = terminateZigEqualSlopeSplit(H);
    const yMid = y0 + H / 2;
    const pts = [
      `${ax.toFixed(2)} ${y0.toFixed(2)}`,
      `${(ax - h * d).toFixed(2)} ${(y0 + b).toFixed(2)}`,
      `${(ax + h * d).toFixed(2)} ${yMid.toFixed(2)}`,
      `${(ax - h * d).toFixed(2)} ${(y0 + H - b).toFixed(2)}`,
      `${ax.toFixed(2)} ${y1.toFixed(2)}`,
    ];
    return terminateZigzagPathD(pts);
  }

  const x0 = ax - halfSpan / sx;
  const x1 = ax + halfSpan / sx;
  const L = x1 - x0;
  const d = amp / sy;
  const b = terminateZigEqualSlopeSplit(L);
  const xMid = x0 + L / 2;
  const pts = [
    `${x0.toFixed(2)} ${ay.toFixed(2)}`,
    `${(x0 + b).toFixed(2)} ${(ay - h * d).toFixed(2)}`,
    `${xMid.toFixed(2)} ${(ay + h * d).toFixed(2)}`,
    `${(x0 + L - b).toFixed(2)} ${(ay - h * d).toFixed(2)}`,
    `${x1.toFixed(2)} ${ay.toFixed(2)}`,
  ];
  return terminateZigzagPathD(pts);
}

function hopTerminateClipId(connectionId: string, trackKey: 'top' | 'bottom') {
  return `hops-term-clip-${connectionId}-${trackKey}`.replace(/[^a-zA-Z0-9_-]/g, '-');
}

/**
 * Clip the cable at the zig tooth line (toward the cable from `ax`), plus cable half-stroke and a
 * modest `meet` for round caps — not past the visible cut (no extra term stroke; mark paints above).
 */
function terminateClipRect(
  direction: 'row' | 'column',
  side: 'start' | 'end',
  ax: number,
  ay: number,
  svgUserScale: HopSvgUserScale | null,
) {
  const sx = svgUserScale?.sx ?? (direction === 'row' ? 6 : 1.25);
  const sy = svgUserScale?.sy ?? (direction === 'row' ? 1.25 : 6);
  const zigExtentRow = HOPS_TERMINATE_ZIG_AMP_PX / sx;
  const zigExtentCol = HOPS_TERMINATE_ZIG_AMP_PX / sy;
  const meetX = HOPS_TERMINATE_CLIP_MEET_PX / sx;
  const meetY = HOPS_TERMINATE_CLIP_MEET_PX / sy;
  const capPadX = HOPS_CABLE_STROKE_PX / 2 / sx;
  const capPadY = HOPS_CABLE_STROKE_PX / 2 / sy;

  if (direction === 'row') {
    if (side === 'start') {
      const toothX = ax + zigExtentRow;
      const minX = toothX - meetX - capPadX;
      return { x: minX, y: -100, width: 200, height: 300 };
    }
    const toothX = ax - zigExtentRow;
    const maxX = toothX + meetX + capPadX;
    return { x: -100, y: -100, width: maxX + 100, height: 300 };
  }
  if (side === 'start') {
    const toothY = ay + zigExtentCol;
    const minY = toothY - meetY - capPadY;
    return { x: -100, y: minY, width: 300, height: 200 };
  }
  const toothY = ay - zigExtentCol;
  const maxY = toothY + meetY + capPadY;
  return { x: -100, y: -100, width: 300, height: maxY + 100 };
}

function hopCurveD(
  direction: 'column' | 'row',
  track: 'top' | 'bottom',
  path: HopConnectionPath = 'arch',
  paired: boolean = false,
) {
  const { p0, cp, p2 } = hopGeometry(direction, track, path, paired);
  return bezierD(p0, cp, p2);
}

const hopCablePath = {
  fill: 'none',
  strokeLinecap: 'round',
  strokeWidth: HOPS_CABLE_STROKE_PX,
  vectorEffect: 'non-scaling-stroke',
};

const HopConnectionPathFlow = styled('path', {
  ...hopCablePath,
  strokeDasharray: '20 78',
  strokeDashoffset: 0,
  animationName: `${hopCableFlow}`,
  animationTimingFunction: 'linear',
  animationIterationCount: 'infinite',

  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 0,
  },

  variants: {
    speed: {
      slow: { animationDuration: '2.8s' },
      normal: { animationDuration: '1.6s' },
      fast: { animationDuration: '0.8s' },
    },
    flow: {
      'start-to-end': { animationDirection: 'normal' },
      'end-to-start': { animationDirection: 'reverse' },
    },
    variant: {
      alternative: {
        stroke: colors.blue300,

        [darkThemeSelector]: {
          stroke: colors.blue700,
        },
      },
      attention: {
        stroke: colors.amber300,

        [darkThemeSelector]: {
          stroke: colors.amber700,
        },
      },
      brand: {
        stroke: colors.brand300,

        [darkThemeSelector]: {
          stroke: colors.brand700,
        },
      },
      negative: {
        stroke: colors.red300,

        [darkThemeSelector]: {
          stroke: colors.red600,
        },
      },
      neutral: {
        stroke: colors.gray200,

        [darkThemeSelector]: {
          stroke: colors.gray600,
        },
      },
      positive: {
        stroke: colors.green300,

        [darkThemeSelector]: {
          stroke: colors.green700,
        },
      },
    },
  },
});
HopConnectionPathFlow.displayName = 'HopConnectionPathFlow';

const HopConnectionPathBase = styled('path', {
  ...hopCablePath,

  variants: {
    kind: {
      empty: {},
      error: {},
      wired: {},
      wireless: {
        strokeDasharray: HOPS_TRACK_WIRELESS_DASH,
      },
    },
    variant: {
      alternative: {
        stroke: colors.blue100,

        [darkThemeSelector]: {
          stroke: colors.blue900,
        },
      },
      attention: {
        stroke: colors.amber100,

        [darkThemeSelector]: {
          stroke: colors.amber900,
        },
      },
      brand: {
        stroke: colors.brand100,

        [darkThemeSelector]: {
          stroke: colors.brand900,
        },
      },
      negative: {
        stroke: colors.red100,

        [darkThemeSelector]: {
          stroke: colors.red800,
        },
      },
      neutral: {
        stroke: colors.gray100,

        [darkThemeSelector]: {
          stroke: colors.gray700,
        },
      },
      positive: {
        stroke: colors.green100,

        [darkThemeSelector]: {
          stroke: colors.green900,
        },
      },
    },
  },
});
HopConnectionPathBase.displayName = 'HopConnectionPathBase';

const HopConnectionPathGradientStroke = styled('path', {
  ...hopCablePath,
  fill: 'none',
});
HopConnectionPathGradientStroke.displayName = 'HopConnectionPathGradientStroke';

const HopTerminationMark = styled('path', {
  fill: 'none',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  strokeWidth: HOPS_TERMINATION_STROKE,
  vectorEffect: 'non-scaling-stroke',
  stroke: colors.gray400,

  [darkThemeSelector]: {
    stroke: colors.gray500,
  },
});
HopTerminationMark.displayName = 'HopTerminationMark';

const HopConnectionSVG = styled('svg', {
  position: 'relative',
  zIndex: hopsVars.zIndex.connection,
  display: 'block',
  overflow: 'visible',

  variants: {
    direction: {
      row: {
        width: '100%',
      },
      column: {
        width: '100%',
        height: '100%',
      },
    },
  },
});
HopConnectionSVG.displayName = 'HopConnectionSVG';

function hopConnectionFlowMaskId(connectionId: string, trackKey: 'top' | 'bottom') {
  return `hops-flow-mask-${connectionId}-${trackKey}`.replace(/[^a-zA-Z0-9_-]/g, '-');
}

function hopConnectionEmptyIds(connectionId: string, trackKey: 'top' | 'bottom') {
  const base = `hops-empty-${connectionId}-${trackKey}`.replace(/[^a-zA-Z0-9_-]/g, '-');
  return { gradientId: `${base}-fade`, maskId: `${base}-mask` };
}

function HopConnectionTrack({
  connectionId,
  d,
  direction,
  paired = false,
  path = 'arch',
  svgUserScale,
  track,
  trackKey,
}: {
  connectionId: string;
  d: string;
  direction: 'column' | 'row';
  paired?: boolean;
  path?: HopConnectionPath;
  svgUserScale?: HopSvgUserScale | null;
  track: HopConnectionTracksProp;
  trackKey: 'top' | 'bottom';
}) {
  const animate = resolveTrackAnimate(track);
  const variant = resolveTrackVariant(track);
  const trackVariantResolved = track.variant;
  const isDualVariant = isTrackDualVariant(trackVariantResolved);
  const dualGradId = `hopdv-${connectionId}-${trackKey}`.replace(/[^a-zA-Z0-9_-]/g, '-');
  const { p0, p2 } = hopGeometry(direction, trackKey, path, paired);
  const flow = track.flow ?? (trackKey === 'top' ? 'start-to-end' : 'end-to-start');
  const showFlow = Boolean(animate);
  const isWireless = track.kind === 'wireless';
  const flowMaskId = hopConnectionFlowMaskId(connectionId, trackKey);

  let trackContent: ReactNode;

  if (isTrackDualVariant(trackVariantResolved)) {
    const { start: dualStart, end: dualEnd } = trackVariantResolved;
    const { styleEl: dualStyleEl, gradientEl: dualGradientEl } = hopDualVariantTrackGradient(
      dualGradId,
      p0,
      p2,
      dualStart,
      dualEnd,
      track.terminates === 'end' ? { terminateEnd: true } : undefined,
    );

    if (track.kind === 'error') {
      const tierPaths = HOPS_ZIGZAG_PATHS[paired ? 'paired' : 'single'][path][direction][trackKey];
      const tierCSS = HOPS_ZIGZAG_TIER_CSS[direction];
      trackContent = (
        <>
          {dualStyleEl}
          <defs>{dualGradientEl}</defs>
          {HOPS_ZIGZAG_TIERS.map((tier, idx) => (
            <HopConnectionPathGradientStroke
              key={tier.teeth}
              d={tierPaths[idx]}
              pathLength={100}
              css={{ stroke: `url(#${dualGradId})`, ...tierCSS[idx] }}
            />
          ))}
        </>
      );
    } else if (track.kind === 'empty') {
      const { gradientId, maskId } = hopConnectionEmptyIds(connectionId, trackKey);
      trackContent = (
        <>
          {dualStyleEl}
          <defs>
            {dualGradientEl}
            <linearGradient
              id={gradientId}
              gradientUnits="userSpaceOnUse"
              x1={p0[0]}
              y1={p0[1]}
              x2={p2[0]}
              y2={p2[1]}
            >
              <stop offset="0" stopColor="white" />
              <stop offset="0.35" stopColor="black" />
              <stop offset="0.65" stopColor="black" />
              <stop offset="1" stopColor="white" />
            </linearGradient>
            <mask
              id={maskId}
              maskUnits="userSpaceOnUse"
              x={-9999}
              y={-9999}
              width={19998}
              height={19998}
            >
              <rect x={-9999} y={-9999} width={19998} height={19998} fill={`url(#${gradientId})`} />
            </mask>
          </defs>
          <HopConnectionPathGradientStroke
            d={d}
            pathLength={100}
            mask={`url(#${maskId})`}
            css={{ stroke: `url(#${dualGradId})` }}
          />
        </>
      );
    } else {
      const dualFlowGradId = `hopdv-flow-${connectionId}-${trackKey}`.replace(
        /[^a-zA-Z0-9_-]/g,
        '-',
      );
      const terminateOpts =
        track.terminates === 'end' ? { terminateEnd: true as const } : undefined;
      const dualFlowGradient =
        showFlow &&
        hopDualVariantFlowGradient(dualFlowGradId, p0, p2, dualStart, dualEnd, terminateOpts);

      trackContent = (
        <>
          {dualStyleEl}
          {dualFlowGradient ? dualFlowGradient.styleEl : null}
          <defs>
            {dualGradientEl}
            {dualFlowGradient ? dualFlowGradient.gradientEl : null}
            {showFlow && isWireless ? (
              <mask
                id={flowMaskId}
                maskUnits="userSpaceOnUse"
                x={-9999}
                y={-9999}
                width={19998}
                height={19998}
              >
                <path
                  d={d}
                  pathLength={100}
                  fill="none"
                  stroke="white"
                  strokeDasharray={HOPS_TRACK_WIRELESS_DASH}
                  strokeLinecap="round"
                  strokeWidth={hopCablePath.strokeWidth}
                  vectorEffect="non-scaling-stroke"
                />
              </mask>
            ) : null}
          </defs>
          <HopConnectionPathGradientStroke
            d={d}
            pathLength={100}
            css={{
              stroke: `url(#${dualGradId})`,
              ...(isWireless ? { strokeDasharray: HOPS_TRACK_WIRELESS_DASH } : {}),
            }}
          />
          {showFlow ? (
            <HopConnectionPathFlow
              d={d}
              pathLength={100}
              speed={animate}
              flow={flow}
              variant={variant}
              css={{ stroke: `url(#${dualFlowGradId})` }}
              mask={isWireless ? `url(#${flowMaskId})` : undefined}
            />
          ) : null}
        </>
      );
    }
  } else if (track.kind === 'error') {
    const tierPaths = HOPS_ZIGZAG_PATHS[paired ? 'paired' : 'single'][path][direction][trackKey];
    const tierCSS = HOPS_ZIGZAG_TIER_CSS[direction];
    trackContent = (
      <>
        {HOPS_ZIGZAG_TIERS.map((tier, idx) => (
          <HopConnectionPathBase
            key={tier.teeth}
            d={tierPaths[idx]}
            pathLength={100}
            kind="error"
            variant={variant}
            css={tierCSS[idx]}
          />
        ))}
      </>
    );
  } else if (track.kind === 'empty') {
    const { gradientId, maskId } = hopConnectionEmptyIds(connectionId, trackKey);

    trackContent = (
      <>
        <defs>
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1={p0[0]}
            y1={p0[1]}
            x2={p2[0]}
            y2={p2[1]}
          >
            <stop offset="0" stopColor="white" />
            <stop offset="0.35" stopColor="black" />
            <stop offset="0.65" stopColor="black" />
            <stop offset="1" stopColor="white" />
          </linearGradient>
          <mask
            id={maskId}
            maskUnits="userSpaceOnUse"
            x={-9999}
            y={-9999}
            width={19998}
            height={19998}
          >
            <rect x={-9999} y={-9999} width={19998} height={19998} fill={`url(#${gradientId})`} />
          </mask>
        </defs>
        <HopConnectionPathBase
          d={d}
          pathLength={100}
          kind="empty"
          variant={variant}
          mask={`url(#${maskId})`}
        />
      </>
    );
  } else {
    trackContent = (
      <>
        <HopConnectionPathBase d={d} pathLength={100} kind={track.kind} variant={variant} />
        {showFlow && !isDualVariant ? (
          <>
            {isWireless && (
              <defs>
                <mask
                  id={flowMaskId}
                  maskUnits="userSpaceOnUse"
                  x={-9999}
                  y={-9999}
                  width={19998}
                  height={19998}
                >
                  <path
                    d={d}
                    pathLength={100}
                    fill="none"
                    stroke="white"
                    strokeDasharray={HOPS_TRACK_WIRELESS_DASH}
                    strokeLinecap="round"
                    strokeWidth={hopCablePath.strokeWidth}
                    vectorEffect="non-scaling-stroke"
                  />
                </mask>
              </defs>
            )}
            <HopConnectionPathFlow
              d={d}
              pathLength={100}
              speed={animate}
              flow={flow}
              variant={variant}
              mask={isWireless ? `url(#${flowMaskId})` : undefined}
            />
          </>
        ) : null}
      </>
    );
  }

  if (track.terminates) {
    const clipId = hopTerminateClipId(connectionId, trackKey);
    const geo = hopGeometry(direction, trackKey, path, paired);
    const tCut =
      track.terminates === 'start' ? HOPS_TERMINATE_INSET / 100 : 1 - HOPS_TERMINATE_INSET / 100;
    const { x: cx, y: cy } = sampleBezier(geo.p0, geo.cp, geo.p2, tCut);
    const { ax, ay } = terminateCutAnchor(
      cx,
      cy,
      direction,
      track.terminates,
      svgUserScale ?? null,
    );
    const clipRect = terminateClipRect(direction, track.terminates, ax, ay, svgUserScale ?? null);
    const cutPath = terminateCutMarkD(cx, cy, direction, track.terminates, svgUserScale ?? null);

    return (
      <>
        <defs>
          <clipPath id={clipId}>
            <rect x={clipRect.x} y={clipRect.y} width={clipRect.width} height={clipRect.height} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>{trackContent}</g>
        <HopTerminationMark d={cutPath} />
      </>
    );
  }

  return trackContent;
}

/* The simple-mode glyph is translucent, so it seats on the breaker chips' solid material. */
const HopConnectionPortContainer = styled('div', {
  position: 'absolute',
  zIndex: hopsVars.zIndex.port,
  display: 'flex',
  padding: '$4',
  background: colors.bgApplicationLight,
  boxShadow: shadows.overlayAppLight,
  borderRadius: '$6',
  pointerEvents: 'none',

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.overlayAppDark,
  },

  variants: {
    direction: {
      row: {
        top: '50%',
        transform: 'translateY(-50%)',
      },
      column: {
        left: '50%',
        transform: 'translateX(-50%)',
      },
    },
    side: {
      start: {},
      end: {},
    },
    size: {
      small: {},
      large: {},
    },
  },

  compoundVariants: [
    {
      direction: 'row',
      side: 'start',
      size: 'small',
      css: { left: `$${hopsVars.icon.row.small / 2}` },
    },
    {
      direction: 'row',
      side: 'start',
      size: 'large',
      css: { left: `$${hopsVars.icon.row.large / 2}` },
    },
    {
      direction: 'row',
      side: 'end',
      size: 'small',
      css: { right: `$${hopsVars.icon.row.small / 2}` },
    },
    {
      direction: 'row',
      side: 'end',
      size: 'large',
      css: { right: `$${hopsVars.icon.row.large / 2}` },
    },
    {
      direction: 'column',
      side: 'start',
      size: 'small',
      css: { top: `$${hopsVars.icon.column.small / 2}` },
    },
    {
      direction: 'column',
      side: 'start',
      size: 'large',
      css: { top: `$${hopsVars.icon.column.large / 2}` },
    },
    {
      direction: 'column',
      side: 'end',
      size: 'small',
      css: { bottom: `$${hopsVars.icon.column.small / 2}` },
    },
    {
      direction: 'column',
      side: 'end',
      size: 'large',
      css: { bottom: `$${hopsVars.icon.column.large / 2}` },
    },
  ],
});
HopConnectionPortContainer.displayName = 'HopConnectionPortContainer';

type HopConnectionPortProps = {
  direction?: 'column' | 'row';
  side?: 'start' | 'end';
  size?: 'small' | 'large';
} & PortProps;

function HopConnectionPort({ direction, side, size, ...remaining }: HopConnectionPortProps) {
  return (
    <HopConnectionPortContainer direction={direction} side={side} size={size}>
      <Port {...remaining} mode="simple" />
    </HopConnectionPortContainer>
  );
}

const HopConnectionBreakerLabel = styled(Text, {
  whiteSpace: 'nowrap',
});
HopConnectionBreakerLabel.displayName = 'HopConnectionBreakerLabel';

const HopConnectionBreakerStack = styled(AlignStack);
HopConnectionBreakerStack.displayName = 'HopConnectionBreakerStack';

const HopConnectionBreakerContainer = styled('div', {
  position: 'absolute',
  zIndex: hopsVars.zIndex.breaker,
  display: 'flex',
  minWidth: '$28',
  minHeight: '$24',
  padding: '$6 $8',
  background: colors.bgApplicationLight,
  boxShadow: shadows.overlayAppLight,
  borderRadius: '$8',
  transition: transitions.focusRing,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.overlayAppDark,
  },

  [selectors.focus]: {
    outline: 'none',
    boxShadow: shadows.focusRingLight,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  variants: {
    orientation: {
      row: {},
      column: {},
    },
    track: {
      top: {},
      bottom: {},
    },
    paired: {
      true: {},
      false: {},
    },
    path: {
      arch: {},
      flat: {},
    },
  },

  defaultVariants: {
    path: 'arch',
  },

  compoundVariants: [
    {
      orientation: 'row',
      track: 'top',
      path: 'arch',
      css: { left: '50%', top: 0, transform: 'translate(-50%, -50%)' },
    },
    {
      orientation: 'row',
      track: 'bottom',
      path: 'arch',
      css: { left: '50%', bottom: 0, transform: 'translate(-50%, 50%)' },
    },
    {
      orientation: 'column',
      track: 'top',
      path: 'arch',
      css: {
        left: `$${hopsVars.padding.target.sides}`,
        top: '50%',
        right: 'auto',
        transform: 'translate(0, -50%)',
      },
    },
    {
      orientation: 'column',
      track: 'bottom',
      path: 'arch',
      css: {
        left: `$${hopsVars.padding.target.sides}`,
        top: '50%',
        right: 'auto',
        transform: 'translate(0, -50%)',
      },
    },

    {
      orientation: 'column',
      track: 'top',
      path: 'arch',
      paired: true,
      css: { top: 'calc(50% - $12)' },
    },
    {
      orientation: 'column',
      track: 'bottom',
      path: 'arch',
      paired: true,
      css: { top: 'calc(50% + $12)' },
    },

    // Flat paths use straight segments at the viewBox midpoint or ±HOPS_PLANE_OFFSET — not the
    // arch apexes, so edge-aligned breaker CSS misses the cable.
    {
      orientation: 'row',
      track: 'top',
      path: 'flat',
      paired: false,
      css: {
        left: '50%',
        top: '50%',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
      },
    },
    {
      orientation: 'row',
      track: 'bottom',
      path: 'flat',
      paired: false,
      css: {
        left: '50%',
        top: '50%',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
      },
    },
    {
      orientation: 'row',
      track: 'top',
      path: 'flat',
      paired: true,
      css: {
        left: '50%',
        top: HOP_FLAT_ROW_TOP_Y_PCT,
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
      },
    },
    {
      orientation: 'row',
      track: 'bottom',
      path: 'flat',
      paired: true,
      css: {
        left: '50%',
        top: HOP_FLAT_ROW_BOTTOM_Y_PCT,
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
      },
    },
    {
      orientation: 'column',
      track: 'top',
      path: 'flat',
      paired: false,
      css: {
        left: `$${hopsVars.padding.target.sides}`,
        top: '50%',
        right: 'auto',
        transform: 'translate(0, -50%)',
      },
    },
    {
      orientation: 'column',
      track: 'bottom',
      path: 'flat',
      paired: false,
      css: {
        left: `$${hopsVars.padding.target.sides}`,
        top: '50%',
        right: 'auto',
        transform: 'translate(0, -50%)',
      },
    },
    {
      orientation: 'column',
      track: 'top',
      path: 'flat',
      paired: true,
      css: {
        left: `$${hopsVars.padding.target.sides}`,
        top: 'calc(50% - $12)',
        right: 'auto',
        transform: 'translate(0, -50%)',
      },
    },
    {
      orientation: 'column',
      track: 'bottom',
      path: 'flat',
      paired: true,
      css: {
        left: `$${hopsVars.padding.target.sides}`,
        top: 'calc(50% + $12)',
        right: 'auto',
        transform: 'translate(0, -50%)',
      },
    },
  ],
});
HopConnectionBreakerContainer.displayName = 'HopConnectionBreakerContainer';

export function HopConnectionSharedPreviewContent({
  payload,
}: {
  payload: HopConnectionPreviewPayload;
}) {
  const { content, label, icon, as, to, viewport, ...previewCardProps } = payload;
  const isPressable = Boolean(as && to);

  return (
    <PreviewCardContent
      gap={12}
      side="bottom"
      sideOffset={8}
      viewport={viewport ?? true}
      {...previewCardProps}
    >
      {/*
        Single child under Viewport so Base UI can attach `data-current` / `data-previous` and
        animate when switching shared-handle triggers.
      */}
      <VStack spacing={12}>
        <AlignStack
          gap={6}
          preset="body"
          start={icon ? <Icon icon={icon} size={14} /> : undefined}
          end={
            isPressable ? (
              <Button
                as={as}
                to={to}
                arrangement="hidden-label"
                ghost
                icon="arrow-right"
                size="small"
                variant="secondary"
              >
                View
              </Button>
            ) : undefined
          }
        >
          <Text weight="bold">{label}</Text>
        </AlignStack>
        <Divider direction="row" />
        {content}
      </VStack>
    </PreviewCardContent>
  );
}

type HopConnectionBreakerProps = {
  arrangement?: BadgeArrangement;
  preview?: HopPreview;
  /** When set with `preview`, only the trigger is rendered; pair with a root `PreviewCard` using this handle. */
  previewHandle?: PreviewCardHandle<HopsPreviewPayload>;
  direction?: 'column' | 'row';
  icon?: IconName;
  label: string;
  paired?: boolean;
  path?: HopConnectionPath;
  tooltip?: TooltipProps;
  track?: 'top' | 'bottom';
  variant?: VariantProp;
  // Routing
  as?: ElementType;
  to?: To;
};

function HopConnectionBreaker({
  arrangement = 'leading-icon',
  preview,
  previewHandle,
  direction,
  icon,
  label,
  paired,
  path = 'arch',
  tooltip,
  track,
  variant,
  // Routing
  as,
  to,
}: HopConnectionBreakerProps) {
  const hopConnectionBreakerIcon = icon ? (
    <Icon icon={icon} size={12} variant={variant} />
  ) : undefined;
  const hopConnectionBreakerLabel = arrangement !== 'hidden-label' && label && (
    <HopConnectionBreakerLabel context="body" variant={variant ?? 'neutral'} weight="bold">
      {label}
    </HopConnectionBreakerLabel>
  );

  const isPressable = Boolean(as && to);

  const hopConnectionBreakerStackProps: ComponentPropsWithoutRef<typeof HopConnectionBreakerStack> =
    {
      direction: arrangement === 'leading-label' ? 'row-reverse' : 'row',
      align: 'center',
      justify: 'center',
      gap: 4,
      width: 'fit-content',
      preset: 'caption',
      start: hopConnectionBreakerIcon,
    };

  const hopConnectionBreakerContainerProps: Omit<
    HopConnectionBreakerProps,
    'label' | 'icon' | 'variant'
  > &
    ComponentPropsWithoutRef<typeof HopConnectionBreakerContainer> = {
    as,
    to,
    orientation: direction,
    tabIndex: isPressable ? 0 : undefined,
    track,
    paired,
    path,
  };

  if (preview && previewHandle) {
    return (
      <PreviewCardTrigger
        closeDelay={300}
        delay={50}
        handle={previewHandle}
        payload={{ kind: 'connection', ...preview, label, icon, as, to }}
        render={({ ref, ...hopConnectionBreakerTriggerProps }) => (
          <HopConnectionBreakerContainer
            ref={ref}
            {...hopConnectionBreakerTriggerProps}
            {...hopConnectionBreakerContainerProps}
          >
            <HopConnectionBreakerStack {...hopConnectionBreakerStackProps}>
              {hopConnectionBreakerLabel}
            </HopConnectionBreakerStack>
          </HopConnectionBreakerContainer>
        )}
      />
    );
  }

  if (preview) {
    return (
      <PreviewCard>
        <PreviewCardTrigger
          render={({ ref, ...hopConnectionBreakerTriggerProps }) => (
            <HopConnectionBreakerContainer
              ref={ref}
              {...hopConnectionBreakerTriggerProps}
              {...hopConnectionBreakerContainerProps}
            >
              <HopConnectionBreakerStack {...hopConnectionBreakerStackProps}>
                {hopConnectionBreakerLabel}
              </HopConnectionBreakerStack>
            </HopConnectionBreakerContainer>
          )}
        />
        <HopConnectionSharedPreviewContent
          payload={{ kind: 'connection', ...preview, label, icon, as, to }}
        />
      </PreviewCard>
    );
  }

  const hopConnectionBreaker = (
    <HopConnectionBreakerContainer {...hopConnectionBreakerContainerProps}>
      <HopConnectionBreakerStack {...hopConnectionBreakerStackProps}>
        {hopConnectionBreakerLabel}
      </HopConnectionBreakerStack>
    </HopConnectionBreakerContainer>
  );

  if (tooltip) {
    return <Tooltip {...tooltip}>{hopConnectionBreaker}</Tooltip>;
  }

  return hopConnectionBreaker;
}

const HopConnectionContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  flex: 1,
  containerType: 'size',

  variants: {
    direction: {
      row: {},
      column: {},
    },
    size: {
      small: {},
      large: {},
    },
  },

  compoundVariants: [
    {
      direction: 'row',
      size: 'small',
      css: { heightAll: `$${hopsVars.padding.target.ends * 2 + hopsVars.icon.row.small}` },
    },
    {
      direction: 'row',
      size: 'large',
      css: { heightAll: `$${hopsVars.padding.target.ends * 2 + hopsVars.icon.row.large}` },
    },
    {
      direction: 'column',
      size: 'small',
      css: {
        widthAll: `$${hopsVars.padding.target.sides * 2 + hopsVars.icon.column.small}`,
        minHeight: `$${hopsVars.icon.column.small}`,
      },
    },
    {
      direction: 'column',
      size: 'large',
      css: {
        widthAll: `$${hopsVars.padding.target.sides * 2 + hopsVars.icon.column.large}`,
        minHeight: `$${hopsVars.icon.column.large}`,
      },
    },
  ],
});
HopConnectionContainer.displayName = 'HopConnectionContainer';

export function HopConnection({
  direction = 'row',
  id,
  path = 'arch',
  ports,
  previewHandle,
  size = 'small',
  tracks,
  width,
  maxWidth,
  minWidth,
  height,
  maxHeight,
  minHeight,
}: HopConnectionProps) {
  const allWireless =
    (!tracks?.top || tracks.top.kind === 'wireless') &&
    (!tracks?.bottom || tracks.bottom.kind === 'wireless');
  const showPorts = !allWireless && ports;

  /* Two 23px port chips, each inset half an icon from an end, overlap inside the bare 60. */
  const effectiveMinHeight = minHeight ?? (showPorts ? 92 : 60);
  const css = merge(
    direction === 'column' ? heightCSS(height) : undefined,
    direction === 'column' ? maxHeightCSS(maxHeight) : undefined,
    direction === 'column' ? minHeightCSS(effectiveMinHeight) : undefined,
    direction === 'row' ? widthCSS(width) : undefined,
    direction === 'row' ? maxWidthCSS(maxWidth) : undefined,
    direction === 'row' ? minWidthCSS(minWidth) : undefined,
  );
  const uid = useId();
  const isRow = direction === 'row';
  const viewBox = isRow ? '0 0 100 40' : '0 0 40 100';
  const paired = path === 'flat' && Boolean(tracks?.top) && Boolean(tracks?.bottom);
  const pathDTop = hopCurveD(direction, 'top', path, paired);
  const pathDBottom = hopCurveD(direction, 'bottom', path, paired);

  const svgRef = useRef<SVGSVGElement>(null);

  const hasTermination = Boolean(tracks?.top?.terminates) || Boolean(tracks?.bottom?.terminates);

  const [svgUserScale, setSvgUserScale] = useState<HopSvgUserScale | null>(null);

  useLayoutEffect(() => {
    if (!hasTermination) {
      setSvgUserScale(null);
      return undefined;
    }
    const svg = svgRef.current;
    if (!svg) return undefined;

    const vbW = isRow ? 100 : 40;
    const vbH = isRow ? 40 : 100;

    const applySize = (w: number, h: number) => {
      if (w <= 0 || h <= 0) return;
      const sx = w / vbW;
      const sy = h / vbH;
      setSvgUserScale((prev) =>
        prev && Math.abs(prev.sx - sx) < 0.001 && Math.abs(prev.sy - sy) < 0.001
          ? prev
          : { sx, sy },
      );
    };

    const measure = () => {
      const w = svg.clientWidth;
      const h = svg.clientHeight;
      if (w > 0 && h > 0) {
        applySize(w, h);
        return;
      }
      const r = svg.getBoundingClientRect();
      applySize(r.width, r.height);
    };

    const ro = new ResizeObserver(() => measure());
    ro.observe(svg);
    measure();
    const raf1 = requestAnimationFrame(measure);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(measure));

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro.disconnect();
    };
  }, [hasTermination, isRow]);

  const hasWirelessFlow =
    (tracks?.top?.kind === 'wireless' && connectionTrackWantsFlowAnimation(tracks.top)) ||
    (tracks?.bottom?.kind === 'wireless' && connectionTrackWantsFlowAnimation(tracks.bottom));

  useEffect(() => {
    if (!hasWirelessFlow) return undefined;
    const svg = svgRef.current;
    if (!svg) return undefined;

    let rafId = 0;
    let lastW = 0;
    let lastH = 0;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width: w, height: h } = entry.contentRect;
      if (w === lastW && h === lastH) return;
      lastW = w;
      lastH = h;

      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        // Browsers can cache the mask bitmap and skip recomputation when
        // the viewport transform changes.  Because the mask paths use
        // vectorEffect="non-scaling-stroke", their rendering is
        // viewport-dependent.  Nudging stroke-opacity forces invalidation
        // of the cached mask so the dashes stay aligned with the base path.
        svg.querySelectorAll('mask path').forEach((p) => {
          const cur = p.getAttribute('stroke-opacity');
          p.setAttribute('stroke-opacity', cur === '0.9999' ? '1' : '0.9999');
        });
      });
    });
    ro.observe(svg);
    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [hasWirelessFlow]);

  const hasTopBreaker = tracks && tracks.top && tracks.top.breaker && tracks.top.breaker.label;
  const hasBottomBreaker =
    tracks && tracks.bottom && tracks.bottom.breaker && tracks.bottom.breaker.label;
  const hasBothBreakers = Boolean(hasTopBreaker && hasBottomBreaker);

  return (
    <HopConnectionContainer id={id} direction={direction} size={size} css={css}>
      {showPorts && ports.start && (
        <HopConnectionPort direction={direction} side="start" size={size} {...ports.start} />
      )}
      {showPorts && ports.end && (
        <HopConnectionPort direction={direction} side="end" size={size} {...ports.end} />
      )}
      {hasTopBreaker && (
        <HopConnectionBreaker
          direction={direction}
          track="top"
          variant={resolveTrackVariant(tracks.top!)}
          {...tracks.top?.breaker}
          label={tracks.top?.breaker?.label ?? ''}
          paired={hasBothBreakers}
          path={path}
          previewHandle={previewHandle}
        />
      )}
      {hasBottomBreaker && (
        <HopConnectionBreaker
          direction={direction}
          track="bottom"
          variant={resolveTrackVariant(tracks.bottom!)}
          label={tracks.bottom?.breaker?.label ?? ''}
          {...tracks.bottom?.breaker}
          paired={hasBothBreakers}
          path={path}
          previewHandle={previewHandle}
        />
      )}
      <HopConnectionSVG
        ref={svgRef}
        aria-hidden
        direction={direction}
        preserveAspectRatio="none"
        viewBox={viewBox}
      >
        {tracks?.top && (
          <HopConnectionTrack
            connectionId={uid}
            d={pathDTop}
            direction={direction}
            paired={paired}
            path={path}
            svgUserScale={hasTermination ? svgUserScale : null}
            track={tracks.top}
            trackKey="top"
          />
        )}
        {tracks?.bottom && (
          <HopConnectionTrack
            connectionId={uid}
            d={pathDBottom}
            direction={direction}
            paired={paired}
            path={path}
            svgUserScale={hasTermination ? svgUserScale : null}
            track={tracks.bottom}
            trackKey="bottom"
          />
        )}
      </HopConnectionSVG>
    </HopConnectionContainer>
  );
}

const HopCollapseButton = styled(Button, {
  position: 'absolute',
  zIndex: hopsVars.zIndex.breaker,
  top: '50%',
  transform: 'translate(-50%, -50%)',

  variants: {
    direction: {
      row: {
        left: '50%',
        transform: 'translate(-50%, -50%)',
      },
      column: {
        left: `$${hopsVars.padding.target.sides}`,
        transform: 'translate(0, -50%)',
      },
    },
  },
});
HopCollapseButton.displayName = 'HopCollapseButton';

const HopCollapseContainer = styled(HopConnectionContainer, {
  variants: {
    direction: {
      row: {
        minWidth: '$220',
      },
      column: {
        minHeight: '$100',
      },
    },
  },
});
HopCollapseContainer.displayName = 'HopCollapseContainer';

export function HopCollapseIndicator({
  direction,
  count,
  onExpand,
}: {
  direction: 'column' | 'row';
  count: number;
  onExpand: () => void;
}) {
  const uid = useId();
  const isRow = direction === 'row';
  const viewBox = isRow ? '0 0 100 40' : '0 0 40 100';
  const pathD = hopCurveD(direction, 'top', 'flat', false);

  return (
    <HopCollapseContainer direction={direction}>
      <HopCollapseButton
        type="button"
        onClick={onExpand}
        direction={direction}
        appearance="muted"
        arrangement="leading-icon"
        icon="chevrons-horizontal"
        size="small"
        variant="primary"
      >
        <Text weight="bold">Show {count} more...</Text>
      </HopCollapseButton>
      <HopConnectionSVG
        aria-hidden
        direction={direction}
        preserveAspectRatio="none"
        viewBox={viewBox}
      >
        <HopConnectionTrack
          connectionId={uid}
          d={pathD}
          direction={direction}
          paired={false}
          path="flat"
          track={{ kind: 'empty', variant: 'neutral' }}
          trackKey="top"
        />
      </HopConnectionSVG>
    </HopCollapseContainer>
  );
}
